import { createContext, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { LinkButton } from '../model/LinkButton'
import { addLinkButton, deleteLinkButton, getLinkButtons, reorderLinkButtons, updateLinkButton } from '../services/api/LinkButtonDataService'
import { getSocialLinks } from "../services/api/SocialLinkDataService"
import { listMoneyLinks, tagMoneyLink, untagMoneyLink } from "../services/api/AnalyticsDataService"


export const LinksContext = createContext()


export const LinksProvider = ({ user, page, children }) => {
    const [openLinkModal, setOpenLinkModal] = useState(false)
    const [openIconModal, setOpenIconModal] = useState(false)
    const [linkButtons, setLinkButtons] = useState([])
    const [socialLinks, setSocialLinks] = useState([])
    const [moneyLinks, setMoneyLinks] = useState({}) // linkButtonId(uuid) -> moneyType

    const pageId = page?.pageId

    useEffect(() => {
        if (!pageId) return
        getLinkButtons(pageId)
            .then(setLinkButtons)
            .catch((error) => console.error("Error fetching link buttons:", error))
    }, [pageId])

    useEffect(() => {
        if (!pageId) return
        listMoneyLinks(pageId)
            .then((rows) => {
                const map = {}
                rows.forEach((r) => { map[r.linkButtonId] = r.moneyType })
                setMoneyLinks(map)
            })
            .catch(() => setMoneyLinks({})) // non-Pro / no tags — leave empty
    }, [pageId])

    useEffect(() => {
        if (!pageId) return
        getSocialLinks(pageId)
            .then(setSocialLinks)
            .catch((error) => console.error("Error fetching social links:", error))
    }, [pageId])


    const handleAddLink = async (newLink) => {
        try {
            const draft = new LinkButton("", linkButtons.length + 1, newLink.title, newLink.url, "")
            const created = await addLinkButton(draft, pageId)
            setLinkButtons((prevButtons) => [...prevButtons, created])
            return created
        } catch (error) {
            console.error("Error adding link button:", error)
        }
    }

    const handleReorderLinks = async (newOrder) => {
        // Optimistically reflect the new order, then persist it as one ordered id list.
        const updatedButtons = newOrder.map((button, index) => ({
            ...button,
            id: index + 1
        }))
        setLinkButtons(updatedButtons)
        try {
            await reorderLinkButtons(pageId, updatedButtons.map((button) => button.uuid))
        } catch (error) {
            console.error("Error reordering link buttons:", error)
        }
    }

    const handleUpdateLink = async (updatedLink) => {
        try {
            const saved = await updateLinkButton(updatedLink)
            setLinkButtons((links) =>
                links.map((link) => (link.uuid === saved.uuid ? saved : link))
            )
        } catch (error) {
            console.error("Error updating link button:", error)
            Swal.fire({ title: "Update failed", text: "Couldn't update your link. Please try again later.", icon: "error" })
        }
    }

    const handleDeleteLink = async (uuid) => {
        try {
            await deleteLinkButton(uuid)
            setLinkButtons(linkButtons.filter(link => link.uuid !== uuid))
        } catch (error) {
            console.error("Error deleting link button:", error)
            Swal.fire({
                title: "Delete failed",
                text: "Couldn't delete your link. Please try again later.",
                icon: "error"
            })
        }
    }

    const handleSocialIconChange = (newSocialIcon) => setSocialLinks(newSocialIcon)

    /** Tag (moneyType set) or untag (moneyType falsy) a link as a money link. Pro-gated server-side. */
    const handleSetMoneyType = async (linkUuid, moneyType) => {
        try {
            if (!moneyType) {
                await untagMoneyLink(linkUuid)
                setMoneyLinks((m) => { const next = { ...m }; delete next[linkUuid]; return next })
            } else {
                await tagMoneyLink(linkUuid, moneyType)
                setMoneyLinks((m) => ({ ...m, [linkUuid]: moneyType }))
            }
        } catch (error) {
            if (error?.response?.status === 403) {
                Swal.fire({
                    title: "A Pro feature",
                    text: "Money links are part of Vibio Pro. Upgrade to tag the links that earn you money.",
                    icon: "info",
                })
            } else {
                Swal.fire({ title: "Couldn't update", text: "Please try again later.", icon: "error" })
            }
        }
    }


    return (
        <LinksContext.Provider value={{
            pageId,
            linkButtons,
            socialLinks,
            moneyLinks,
            openLinkModal,
            setOpenLinkModal,
            openIconModal,
            setOpenIconModal,
            handleAddLink,
            handleReorderLinks,
            handleUpdateLink,
            handleDeleteLink,
            handleSocialIconChange,
            handleSetMoneyType,
        }}>
            {children}
        </LinksContext.Provider>
    )
}
